import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";
import { BRAND_SHORT } from "@/lib/brand";

export default function MFA() {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [enabled, setEnabled] = useState(null);
  const [recovery, setRecovery] = useState([]);
  const [busy, setBusy] = useState(false);

  const begin = async () => {
    setBusy(true);
    try { const r = await api.post("/mfa/setup"); setSetup(r.data); toast.success("Scan the QR with your authenticator"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mfa/verify", { code });
      setEnabled(true); setRecovery(r.data.recovery_codes || []);
      toast.success("MFA enabled");
    } catch (e) { toast.error("Invalid code"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    await api.post("/mfa/disable"); setEnabled(false); setSetup(null); setRecovery([]); toast.success("MFA disabled");
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">MFA &amp; Identity Security</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Multi-Factor Authentication</h1>
        <p className="text-sm text-zinc-500 mt-1">TOTP via Microsoft / Google Authenticator (PyOTP). Recovery codes generated on enable.</p>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-6">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck size={16} className="text-emerald-400"/><div className="font-display font-medium">{BRAND_SHORT} TOTP</div></div>
        {!setup && !enabled && (
          <button onClick={begin} disabled={busy} data-testid="mfa-setup-btn" className="bg-blue-600 hover:bg-blue-700 text-white text-sm rounded px-4 py-2">{busy?"…":"Start enrollment"}</button>
        )}
        {setup && !enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="MFA QR" className="rounded-md bg-white p-3 max-w-[220px]" />
              <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Manual secret</div>
              <code className="block bg-zinc-900 border border-zinc-800 rounded p-2 text-xs font-mono mt-1 break-all">{setup.secret}</code>
            </div>
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Enter 6-digit code</div>
              <input data-testid="mfa-code" value={code} onChange={e=>setCode(e.target.value)} maxLength={6} className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-lg font-mono tracking-widest"/>
              <button onClick={verify} disabled={busy || code.length !== 6} data-testid="mfa-verify-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded px-4 py-2 disabled:opacity-60">{busy?"Verifying…":"Verify &amp; enable"}</button>
            </div>
          </div>
        )}
        {enabled && (
          <div className="space-y-3">
            <div className="text-emerald-400 font-mono text-sm">● MFA enabled on this account.</div>
            {recovery.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2"><KeyRound size={14}/><div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Recovery codes (save these once)</div></div>
                <div className="grid grid-cols-4 gap-2">{recovery.map(c => <code key={c} className="bg-zinc-900 border border-zinc-800 rounded p-2 font-mono text-xs">{c}</code>)}</div>
              </div>
            )}
            <button onClick={disable} className="bg-red-600/80 hover:bg-red-600 text-white text-sm rounded px-4 py-2">Disable MFA</button>
          </div>
        )}
      </div>
    </div>
  );
}
